/**
 * Bounded fan-out for per-session server calls.
 *
 * Import/Export walks every chat the user has: one DELETE per session when
 * clearing, one GET per session when hydrating a backup. `Promise.all` over
 * that list is wrong twice over — it opens as many concurrent requests as the
 * account has chats (hundreds, for a heavy user), and it rejects on the first
 * failure while the rest stay in flight, so the caller cannot tell which of
 * the calls actually happened.
 */

/** How many per-session requests Import/Export keeps in flight at once. */
export const SERVER_FANOUT_LIMIT = 6;

/**
 * Run `task` over `items` with at most `limit` calls in flight, settling every
 * one. Results keep the order of `items`; a rejection is reported in place
 * rather than aborting the queue, so the caller can count the failures and
 * still know what succeeded.
 */
export async function mapSettledWithLimit<T, R>(
  items: T[],
  limit: number,
  task: (item: T) => Promise<R>
): Promise<PromiseSettledResult<R>[]> {
  const results = new Array<PromiseSettledResult<R>>(items.length);
  let next = 0;

  const worker = async () => {
    while (next < items.length) {
      const index = next++;
      try {
        results[index] = { status: 'fulfilled', value: await task(items[index]) };
      } catch (reason) {
        results[index] = { status: 'rejected', reason };
      }
    }
  };

  const workers = Array.from(
    { length: Math.max(1, Math.min(limit, items.length)) },
    () => worker()
  );
  await Promise.all(workers);

  return results;
}
